class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.installCandyshopBottomSheetStyles();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.ensureChoiceButtons();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.syncCustomCartBadge());
    }

    document.addEventListener('candyshop:cart-updated', () => this.refreshAndOpen());
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  installCandyshopBottomSheetStyles() {
    if (document.getElementById('CandyshopBottomCartStyles')) return;
    const style = document.createElement('style');
    style.id = 'CandyshopBottomCartStyles';
    style.textContent = `
      cart-drawer.drawer {
        align-items: flex-end !important;
        justify-content: center !important;
        background: rgba(15,33,56,.46) !important;
      }
      cart-drawer .drawer__inner {
        position: relative !important;
        width: min(76rem, calc(100vw - 2.4rem)) !important;
        max-width: none !important;
        height: auto !important;
        max-height: min(82vh, 76rem) !important;
        padding: 1.2rem 2rem 0 !important;
        border: 1px solid rgba(15,33,56,.10) !important;
        border-bottom: 0 !important;
        border-radius: 2.4rem 2.4rem 0 0 !important;
        box-shadow: 0 -1.4rem 4rem rgba(15,33,56,.20) !important;
        transform: translateY(105%) !important;
        transition: transform .28s cubic-bezier(.22,.8,.24,1) !important;
        overflow: hidden !important;
      }
      cart-drawer .drawer__inner::before {
        content: '';
        display: block;
        width: 4.6rem;
        height: .45rem;
        margin: 0 auto .7rem;
        border-radius: 999px;
        background: rgba(15,33,56,.18);
        flex: 0 0 auto;
      }
      cart-drawer.drawer.active .drawer__inner {
        transform: translateY(0) !important;
      }
      cart-drawer .drawer__header {
        padding: .8rem 0 1rem !important;
      }
      cart-drawer .drawer__heading {
        margin: 0 !important;
        font-weight: 800 !important;
      }
      cart-drawer cart-drawer-items {
        max-height: 48vh !important;
        min-height: 0 !important;
      }
      cart-drawer .drawer__footer {
        padding: 1.2rem 0 max(1.6rem, env(safe-area-inset-bottom)) !important;
      }
      cart-drawer .cart__ctas {
        display: grid !important;
        grid-template-columns: 1fr 1fr !important;
        gap: 1rem !important;
        margin-top: 1rem !important;
      }
      cart-drawer #CartDrawer-Checkout,
      cart-drawer .candyshop-cart-continue {
        width: 100% !important;
        min-height: 4.9rem !important;
        margin: 0 !important;
        border-radius: 999px !important;
        font-weight: 800 !important;
      }
      cart-drawer .candyshop-cart-continue {
        border: 1px solid rgba(15,33,56,.18) !important;
        background: #fff !important;
        color: #0f2138 !important;
        box-shadow: none !important;
      }
      cart-drawer .candyshop-cart-continue:hover {
        background: #f7fafb !important;
      }
      @media (max-width: 749px) {
        cart-drawer .drawer__inner {
          width: 100% !important;
          max-width: 100% !important;
          max-height: 88vh !important;
          padding: 1rem 1.4rem 0 !important;
          border-left: 0 !important;
          border-right: 0 !important;
          border-radius: 2rem 2rem 0 0 !important;
        }
        cart-drawer cart-drawer-items {
          max-height: 46vh !important;
        }
        cart-drawer .cart__ctas {
          grid-template-columns: 1fr !important;
          gap: .8rem !important;
        }
        cart-drawer #CartDrawer-Checkout { order: 1; }
        cart-drawer .candyshop-cart-continue { order: 2; }
      }
    `;
    document.head.appendChild(style);
  }

  ensureChoiceButtons() {
    const checkout = this.querySelector('#CartDrawer-Checkout');
    const ctas = this.querySelector('.cart__ctas');
    if (!checkout || !ctas || this.classList.contains('is-empty') || checkout.disabled) return;

    checkout.textContent = 'Zur Kasse';

    let continueButton = ctas.querySelector('.candyshop-cart-continue');
    if (!continueButton) {
      continueButton = document.createElement('button');
      continueButton.type = 'button';
      continueButton.className = 'button button--secondary candyshop-cart-continue';
      continueButton.textContent = 'Weiter shoppen';
      continueButton.setAttribute('aria-label', 'Weiter shoppen');
      continueButton.addEventListener('click', () => this.close());
      ctas.appendChild(continueButton);
    }
  }

  setHeaderCartIconAccessibility() {
    const links = document.querySelectorAll('#cart-icon-bubble, .csx-cart');
    links.forEach((cartLink) => {
      if (cartLink.dataset.candyAjaxCartReady === '1') return;
      cartLink.dataset.candyAjaxCartReady = '1';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code !== 'Space') return;
        event.preventDefault();
        this.open(cartLink);
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.ensureChoiceButtons();
    setTimeout(() => this.classList.add('animate', 'active'));
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const sectionHtml = parsedState.sections && parsedState.sections[section.id];
      if (!sectionElement || !sectionHtml) return;
      const inner = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (inner !== null) sectionElement.innerHTML = inner;
    });

    this.classList.remove('is-empty');
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.syncCustomCartBadge();
    this.setHeaderCartIconAccessibility();
    this.ensureChoiceButtons();
    this.open();
  }

  refreshAndOpen() {
    fetch(`${window.routes.cart_url}?section_id=cart-drawer`, { headers: { Accept: 'text/html' } })
      .then((response) => response.text())
      .then((htmlText) => {
        const doc = new DOMParser().parseFromString(htmlText, 'text/html');
        const fresh = doc.querySelector('#CartDrawer');
        const current = this.querySelector('#CartDrawer');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        this.classList.toggle('is-empty', !fresh || !fresh.querySelector('.cart-item'));
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
        this.setHeaderCartIconAccessibility();
        this.syncCustomCartBadge();
        this.ensureChoiceButtons();
        this.open();
      })
      .catch((error) => console.error('Candyshop AJAX Warenkorb:', error));
  }

  syncCustomCartBadge() {
    fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        document.querySelectorAll('.csx-cart').forEach((link) => {
          let badge = link.querySelector('b');
          if (cart.item_count > 0) {
            if (!badge) { badge = document.createElement('b'); link.appendChild(badge); }
            badge.textContent = cart.item_count;
          } else if (badge) badge.remove();
        });
      })
      .catch(() => {});
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  setActiveElement(element) { this.activeElement = element; }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);
